/**
 * Cluster Stability Mock
 *
 * Visualises DBSCAN-style cluster extraction from an
 * optimisation landscape. Shows a primary robust cluster,
 * an alternate cluster, and excluded outlier points.
 */

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';

// Simulated 2D parameter space with clusters
const primaryCluster = [
  { x: 45, y: 52 }, { x: 48, y: 55 }, { x: 50, y: 50 },
  { x: 47, y: 48 }, { x: 52, y: 53 }, { x: 49, y: 56 },
  { x: 51, y: 49 }, { x: 46, y: 51 }, { x: 53, y: 54 },
  { x: 48, y: 47 }, { x: 50, y: 55 }, { x: 44, y: 50 },
];

const alternateCluster = [
  { x: 25, y: 72 }, { x: 27, y: 75 }, { x: 23, y: 70 },
  { x: 26, y: 73 }, { x: 28, y: 71 },
];

const outliers = [
  { x: 10, y: 30 }, { x: 80, y: 85 }, { x: 75, y: 20 },
  { x: 15, y: 78 }, { x: 68, y: 40 }, { x: 35, y: 15 },
  { x: 85, y: 60 }, { x: 20, y: 45 },
];

const centroid = { x: 48.6, y: 51.7 };

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

export default function ClusterStabilityMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Cluster-Based Stability Extraction
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height={170}>
              <ScatterChart margin={{ top: 8, right: 8, bottom: 16, left: 4 }}>
                <CartesianGrid stroke="#1e293b" strokeWidth={0.5} />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={[5, 90]}
                  tick={{ fill: '#64748b', fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: 'EMA Period', position: 'insideBottom', offset: -8, fill: '#64748b', fontSize: 10 }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  domain={[10, 90]}
                  tick={{ fill: '#64748b', fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: 'RSI Period', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10, offset: 10 }}
                />
                <Tooltip contentStyle={tipStyle} formatter={(v: number, name: string) => [v, name === 'x' ? 'EMA' : 'RSI']} />
                <ReferenceLine x={centroid.x} stroke="#22c55e" strokeWidth={1} strokeDasharray="3 3" />
                <ReferenceLine y={centroid.y} stroke="#22c55e" strokeWidth={1} strokeDasharray="3 3" />
                <Scatter name="Outliers" data={outliers} isAnimationActive={false}>
                  {outliers.map((_, i) => <Cell key={i} fill="#475569" opacity={0.5} />)}
                </Scatter>
                <Scatter name="Alternate" data={alternateCluster} isAnimationActive={false}>
                  {alternateCluster.map((_, i) => <Cell key={i} fill="#3b82f6" opacity={0.7} />)}
                </Scatter>
                <Scatter name="Primary" data={primaryCluster} isAnimationActive={false}>
                  {primaryCluster.map((_, i) => <Cell key={i} fill="#22c55e" opacity={0.8} />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Legend panel */}
          <div style={{ width: 100, paddingTop: '0.5rem', fontSize: '0.5rem' }}>
            {[
              { color: '#22c55e', label: 'Primary (12 pts)' },
              { color: '#3b82f6', label: 'Alternate (5 pts)' },
              { color: '#475569', label: 'Outliers' },
              { color: '#22c55e', label: 'Centroid', marker: true },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.375rem' }}>
                {item.marker ? (
                  <div style={{ width: 8, height: 8, border: `1.5px solid ${item.color}`, borderRadius: '50%' }} />
                ) : (
                  <div style={{ width: 8, height: 8, backgroundColor: item.color, borderRadius: '50%', opacity: 0.7 }} />
                )}
                <span style={{ color: '#94a3b8' }}>{item.label}</span>
              </div>
            ))}
            <div style={{
              marginTop: '0.5rem', padding: '0.375rem',
              background: '#1e293b', borderRadius: 4, border: '1px solid #334155',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '0.4375rem', color: '#64748b' }}>Robustness Score</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#22c55e', fontFamily: 'ui-monospace, monospace' }}>78 / 100</div>
            </div>
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        The primary cluster is selected by density and consistency, not peak performance.
      </div>
    </div>
  );
}
